/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, Subscription } from 'rxjs';
import { GenericStompConnection } from './stomp-manager/generic-stomp-connection';
import { EnvironmentService } from './environment.service';
import { HealthService } from './health.service';
import { ServiceName } from '../../../assets/config/environment';
import { selectToken } from '../../user/+state/user.selectors';

/** Manages the one-shot STOMP connection to apollo_observability-proxy for log streaming. */
@Injectable({ providedIn: 'root' })
export class ObservabilityProxyStompService extends HealthService implements OnDestroy {
    private connection: GenericStompConnection | null = null;
    private subscription: Subscription | null = null;
    private connectionUrl: string | null = null;

    constructor(
        protected http: HttpClient,
        private readonly store: Store,
        private readonly env: EnvironmentService
    ) {
        super(
            http,
            ServiceName.OBSERVABILITY_PROXY,
            env.environment?.SERVICES?.[ServiceName.OBSERVABILITY_PROXY] ?? [],
            '/api/actuator/health/liveness',
            !!env.environment?.SERVICES?.[ServiceName.OBSERVABILITY_PROXY]?.length
        );
    }

    /**
     * Establishes the STOMP connection once the service is configured, health initialization has completed,
     * and both an active URL and a token are available. Re-evaluates whenever any of these change.
     * No-op if already enabled.
     */
    enable(): void {
        if (this.subscription) {
            return;
        }

        if (!this.env.environment?.SERVICES?.[ServiceName.OBSERVABILITY_PROXY]?.length) {
            console.error('[ObservabilityProxy] Service URL not configured — log streaming unavailable');
            return;
        }

        this.subscription = combineLatest([
            this.initialized$.pipe(filter((initialized) => initialized)),
            this.activeUrl$.pipe(
                filter((url): url is string => !!url),
                distinctUntilChanged()
            ),
            this.store.select(selectToken).pipe(
                filter((token): token is string => !!token),
                distinctUntilChanged()
            )
        ]).subscribe(([, url, token]) => this.connect(url, token));
    }

    /** Tears down the STOMP connection. Health checks keep running so failover still works if re-enabled. */
    disable(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.connection?.disconnectWebSocket();
        this.connection = null;
        this.connectionUrl = null;
    }

    public connected(): boolean {
        return !!this.connection?.connected();
    }

    /**
     * Publishes a single log entry to /to/logs on the observability proxy.
     * Throws if the connection is not open so the caller can retry the entry.
     */
    publishLog(entry: string): void {
        if (!this.connection?.connected()) {
            throw new Error('[ObservabilityProxy] Not connected — log entry not published');
        }
        this.connection.publish({
            destination: '/to/logs',
            body: entry
        });
    }

    private connect(url: string, token: string): void {
        if (this.connection && this.connectionUrl === url) {
            this.connection.updateToken(token);
            return;
        }

        this.connection?.disconnectWebSocket();
        this.connectionUrl = url;
        this.connection = new GenericStompConnection(url, token);
        this.connection.setConnection();
    }

    ngOnDestroy(): void {
        this.disable();
        this.cancelScheduledHealthChecks();
    }
}
